/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import { useEffect, useState, useMemo } from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { TextareaAutosize } from "@mui/material";
import Box from "@mui/material/Box";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { rejectUserRequest } from "../../../../Services/redux/global-admin-users/GlobalAdminUsersActions";
import {
  modalTitleStyles,
  modalBodyTextStyles,
  fieldLabelStyles,
  requiredAsteriskStyles,
} from "./UserModalStyles";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const StyledTextarea = styled(TextareaAutosize)({
  resize: "vertical",
  maxWidth: "423px",
  minHeight: "64px",
  padding: "12px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid #12141533",
  "::placeholder": {
    color: "#717273",
    fontFamily: "Roboto",
    fontWeight: 400,
    fontSize: "14px",
    letterSpacing: "0px",
    lineHeight: "140%",
    verticalAlign: "middle",
  },
});

export default function RejectUserModal({
  openRejectUserModal,
  handleRejectUserClose,
  searchTerm,
  selectedRowId,
}) {
  const dispatch = useDispatch();
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    if (!openRejectUserModal) {
      setRejectReason("");
    }
  }, [openRejectUserModal]);

  const handleRejectUserRequest = () => {
    // Only proceed if there's a valid reason (not empty or just whitespace)
    if (rejectReason.trim()) {
      //API call to reject user with reason
      dispatch(
        rejectUserRequest(
          { reason: rejectReason.trim() },
          selectedRowId,
          searchTerm.trim()
        )
      );
      //Close modal after successful API call
      handleRejectUserClose();
    }
  };

  // Check if the reject reason is valid (not empty or just whitespace)
  const isReasonValid = useMemo(
    () => rejectReason.trim().length > 0,
    [rejectReason]
  );

  return (
      <BootstrapDialog
        onClose={handleRejectUserClose}
        aria-labelledby="customized-dialog-title"
        open={openRejectUserModal}
        slotProps={{
          paper: {
            sx: {
              width: "423px",
              minHeight: "196px",
              borderRadius: "8px",
              padding: "24px",
              display: "flex",
              gap: "24px",
            },
          },
        }}
      >
        <Box
          sx={{
            minWidth: "375px",
            minHeight: "88px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <div>
            <Typography
              sx={modalTitleStyles}
            >
              Reject New User
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleRejectUserClose}
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <Box
            sx={{
              maxWidth: "423px",
              minHeight: "87px",
              display: "flex",
              flexDirection: "column",
              gap: "36px",
            }}
          >
            <Typography
              sx={modalBodyTextStyles}
            >
              The person will be notified by email. You should provide a
              rejection reason and it will be added into a notification message
              body.
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                maxWidth: "423px",
                minHeight: "87px",
              }}
            >
              <Typography
                component="label"
                sx={fieldLabelStyles}
              >
                Reason to Reject
                {" "}
                <span style={requiredAsteriskStyles}>*</span>
              </Typography>
              <StyledTextarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Prompt text"
              />
            </Box>
          </Box>
        </Box>

        <DialogActions>
          <Button
            sx={{
              textTransform: "none",
            }}
            variant="outlined"
            onClick={handleRejectUserClose}
          >
            Cancel
          </Button>
          <Button
            sx={{
              textTransform: "none",
              color: "#B50B00",
              borderColor: "#B50B00",
              opacity: isReasonValid ? 1 : 0.5,
            }}
            variant="outlined"
            onClick={handleRejectUserRequest}
            disabled={!isReasonValid}
          >
            Reject
          </Button>
        </DialogActions>
      </BootstrapDialog>
  );
}

RejectUserModal.propTypes = {
  openRejectUserModal: PropTypes.bool,
  handleRejectUserClose: PropTypes.func,
  searchTerm: PropTypes.string,
  selectedRowId: PropTypes.string,
};
